import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import InsightChatFeedback from "./InsightChatFeedback";
import MarkdownFormatter from "@/components/common/markdown/MarkdownFormatter";
import { isImageFile } from "@/screens/utils/Helpers";
import { useRef, useState } from "react";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import SelectedSourcesPopover from "./dropdown/SelectedSourcesPopover";
import { FileAttachmentsPreview } from "@/components/hoc/fileAttachment";
import { Agent, InsightAccount, MessageFeedback, MessageType } from "../types";
import { File } from "@/types";
import { RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";
import CreativeAttachmentCard from "./CreativeAttachmentCard";

const extractTables = (
  content: string,
): {
  tables: { tableContent: string; startIndex: number }[];
  remainingContent: string;
} => {
  const tables: { tableContent: string; startIndex: number }[] = [];
  const lines = content.split("\n");
  let currentTable = "";
  let isInTable = false;
  let tableStartIndex = 0;
  const tableLines: number[] = [];

  lines.forEach((line, index) => {
    const isTableRow = line.trim().startsWith("|");

    if (isTableRow && !isInTable) {
      isInTable = true;
      tableStartIndex = index;
      currentTable = line + "\n";
      tableLines.push(index);
    } else if (isTableRow && isInTable) {
      currentTable += line + "\n";
      tableLines.push(index);
    } else if (!isTableRow && isInTable) {
      isInTable = false;
      if (currentTable.split("\n").length > 2) {
        // Ensure it's a valid table
        tables.push({
          tableContent: currentTable.trim(),
          startIndex: tableStartIndex,
        });
      }
      currentTable = "";
    }
  });

  // Handle case where table is at the end of content
  if (isInTable && currentTable.split("\n").length > 2) {
    tables.push({
      tableContent: currentTable.trim(),
      startIndex: tableStartIndex,
    });
  }

  // Filter out table lines from the content
  const remainingContent = lines
    .filter((_, index) => !tableLines.includes(index))
    .join("\n");

  return { tables, remainingContent };
};

const extractMedia = (
  content: string,
): { url: string; alt: string; type: "image" | "video" }[] => {
  // Match both markdown images ![alt](url) and regular links [text](url) that contain media URLs
  const mediaRegex = /(?:!\[([^\]]*)\]|(\[([^\]]*)\]))\(([^)]+)\)/g;
  const media: { url: string; alt: string; type: "image" | "video" }[] = [];
  let match;

  while ((match = mediaRegex.exec(content)) !== null) {
    const url = match[4]; // The URL is always in the 4th capture group
    const alt = match[1] || match[3] || ""; // Alt text from either ![alt] or [text] format

    // Check if URL looks like an image or video
    if (url) {
      const isImage =
        /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff?)(\?|$)/i.test(url) ||
        url.includes("image") ||
        url.includes("img") ||
        url.includes("jpg") ||
        url.includes("png") ||
        url.includes("gif");

      const isVideo =
        /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v|3gp|ogv)(\?|$)/i.test(url) ||
        url.includes("video") ||
        url.includes("vid") ||
        url.includes("mp4") ||
        url.includes("mov") ||
        url.includes("avi");

      if (isImage) {
        media.push({ url, alt, type: "image" });
      } else if (isVideo) {
        media.push({ url, alt, type: "video" });
      }
    }
  }
  return media;
};

const convertMediaToFiles = (
  media: { url: string; alt: string; type: "image" | "video" }[],
): File[] => {
  return media.map((item, index) => ({
    id: `extracted-media-${index}`,
    file_name: item.alt || `${item.type === "image" ? "Image" : "Video"} ${index + 1}`,
    public_url: item.url,
  }));
};

const convertTableToCSV = (tableContent: string) => {
  const rows = tableContent
    .split("\n")
    .filter((row) => row.trim())
    .map((row) =>
      row
        .split("|")
        .filter((cell) => cell.trim())
        .map((cell) => {
          // Clean the cell content and handle quotes
          const cleanCell = cell.trim().replace(/\s+/g, " ");
          return cleanCell.includes(",") ? `"${cleanCell}"` : cleanCell;
        }),
    );

  return rows.map((row) => row.join(",")).join("\n");
};

export default function MessageCard({
  chatId,
  msg,
  index,
  totalMessages,
  handleUpdateFeedback,
  showIcon,
}: {
  selectedAccount: InsightAccount | undefined;
  chatId: string;
  msg: MessageType;
  index: number;
  totalMessages: number;
  handleUpdateFeedback: (feedback: MessageFeedback, id: string) => void;
  showIcon?: boolean;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showThinking, setShowThinking] = useState(false);

  const onIntersect = (entry: IntersectionObserverEntry) => {
    if (entry.intersectionRatio < 1 && isOpen) {
      setIsOpen(false);
    }
  };

  useIntersectionObserver(popoverRef, onIntersect, {
    threshold: 1,
  });

  const response = msg.answer || msg.response;

  return (
    <div
      key={msg.chat_conversation_id || index}
      className={cn(
        "px-5 py-4 text-sm break-words max-w-[850px] w-[99%]",
        index === totalMessages - 1 ? "" : "border-b",
        msg.agent === Agent.user ? "bg-white/90" : "pb-12",
      )}
    >
      {msg.agent === Agent.user && (
        <div className={`flex justify-between w-full`}>
          <span
            className={`text-[12px] font-medium leading-[12px] text-brand-primary-600`}
          >
            You
          </span>
          <span className="text-[12px] font-normal leading-[10px] text-brand-primary-600">
            {format(msg.created_at, "hh:mm a")}
          </span>
        </div>
      )}
      {/* Show creative cards only for user messages - assistant creatives are in the sources popover */}
      {msg.agent === Agent.user && msg.creatives && msg.creatives.length > 0 && (
        <div className="mt-3 w-full overflow-x-auto overflow-y-hidden flex gap-2 scrollbar-hide">
          {msg.creatives.map((creative) => (
            <CreativeAttachmentCard
              key={creative.id}
              name={creative.name}
              thumbnailUrl={creative.thumbnail_url}
              mediaUrl={creative.media_url}
              type={creative.type}
              metrics={msg.metrics}
            />
          ))}
        </div>
      )}
      <div className="relative mt-3">
        {msg.agent === Agent.assistant ? (
          // New two-part response format
          <div className="relative" ref={contentRef}>
            {/* Reasoning section - collapsible */}
            <div className="flex items-center justify-between mb-3">
              <div className="relative flex items-start">
                {msg.reasoning && (
                  <Collapsible open={showThinking} onOpenChange={setShowThinking}>
                    <div className="flex items-center gap-2">
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-[6px] cursor-pointer h-[32px]">
                          {(() => {
                            const getLatestHeading = () => {
                              if (!msg.reasoning) return "Gathering context";
                              // Use multiline regex to find all headings starts at new line and ends before new line
                              const matches = [
                                ...msg.reasoning.matchAll(/^\*\*([^*]+)\*\*\s*$/gm),
                              ];
                              return matches.length > 0
                                ? matches[matches.length - 1][1]
                                : "Gathering context";
                            };
                            const latestHeading = getLatestHeading();
                            const isAnswerStarted = !!msg.answer;
                            return (
                              <span
                                className={`text-[14px] font-normal text-brand-primary-new  ${!isAnswerStarted ? "shimmer-text" : ""}`}
                                data-text={
                                  !isAnswerStarted ? latestHeading : "show_thinking"
                                }
                              >
                                {!isAnswerStarted ? latestHeading : "Show thinking"}
                              </span>
                            );
                          })()}
                          {showThinking ? (
                            <RiArrowUpSLine
                              size={14}
                              className="text-[#9aa3bd] shrink-0"
                            />
                          ) : (
                            <RiArrowDownSLine
                              size={14}
                              className="text-[#9aa3bd] shrink-0"
                            />
                          )}
                        </div>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="mt-2">
                      <div className="ml-4 mb-2 pl-[18px] border-l border-border-gray">
                        {(() => {
                          if (!msg.reasoning) return null;
                          const { tables } = extractTables(msg.reasoning);
                          const lines = msg.reasoning.split("\n");
                          const contentParts = [];
                          let currentIndex = 0;
                          let currentPosition = 0;

                          // Process content and tables in order of appearance
                          while (currentPosition < lines.length) {
                            const nextTable = tables.find(
                              (t) => t.startIndex >= currentPosition,
                            );
                            const endPosition = nextTable
                              ? nextTable.startIndex
                              : lines.length;
                            const textContent = lines
                              .slice(currentPosition, endPosition)
                              .join("\n");

                            if (textContent.trim()) {
                              contentParts.push(
                                <MarkdownFormatter
                                  key={`reasoning-content-${currentIndex++}`}
                                  content={textContent}
                                  className="text-[12px] text-brand-primary-800 font-normal prose-strong:font-medium"
                                />,
                              );
                            }

                            if (nextTable) {
                              contentParts.push(
                                <div
                                  key={`reasoning-table-${currentIndex++}`}
                                  className="mb-4"
                                >
                                  <div className="overflow-x-auto max-w-full">
                                    <div className="inline-block min-w-full align-middle">
                                      <MarkdownFormatter
                                        content={nextTable.tableContent}
                                        className="text-[12px] text-brand-primary-800 font-normal prose-strong:font-medium"
                                      />
                                    </div>
                                  </div>
                                </div>,
                              );
                              currentPosition =
                                nextTable.startIndex +
                                nextTable.tableContent.split("\n").length;
                            } else {
                              currentPosition = lines.length;
                            }
                          }

                          return contentParts.length > 0 ? (
                            contentParts
                          ) : (
                            <MarkdownFormatter
                              content={msg.reasoning}
                              className="text-[12px] text-brand-primary-800 font-normal prose-strong:font-medium"
                            />
                          );
                        })()}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
                {/* Loading state - show loader with loading message */}
                {msg.agent === Agent.assistant && msg.status === "loading" && (
                  <span
                    className="text-[14px] text-brand-primary-new/80 translate-y-[1px] shimmer-text h-[32px] text-center leading-[32px]"
                    data-text={msg.text || "Gathering context"}
                  >
                    {msg.text || "Gathering context"}
                  </span>
                )}
              </div>
              <div className="absolute right-0 top-0">
                {/* show source popover */}
                {msg?.metrics?.length ||
                msg?.sections?.length ||
                msg?.creatives?.length ? (
                  <SelectedSourcesPopover
                    msg={msg}
                    showIcon={showIcon}
                    sections={msg?.sections ?? []}
                    metrics={msg?.metrics ?? []}
                    creatives={msg?.creatives}
                  />
                ) : null}
              </div>
            </div>

            {/* Answer section */}
            {response && (
              <div className={cn(msg.reasoning ? "mt-2" : "mt-10")}>
                {(() => {
                  const { tables } = extractTables(response);
                  const media = extractMedia(response);
                  const lines = response.split("\n");
                  const contentParts = [];
                  let currentIndex = 0;
                  let currentPosition = 0;

                  // Process content and tables in order of appearance
                  while (currentPosition < lines.length) {
                    const nextTable = tables.find((t) => t.startIndex >= currentPosition);
                    const endPosition = nextTable ? nextTable.startIndex : lines.length;
                    const textContent = lines
                      .slice(currentPosition, endPosition)
                      .join("\n");

                    if (textContent.trim()) {
                      contentParts.push(
                        <MarkdownFormatter
                          key={`answer-content-${currentIndex++}`}
                          content={textContent}
                          className="text-[14px] text-brand-primary-800 font-normal prose-strong:font-medium"
                        />,
                      );
                    }

                    if (nextTable) {
                      contentParts.push(
                        <div key={`answer-table-${currentIndex++}`} className="mb-4">
                          <div className="overflow-x-auto max-w-full">
                            <div className="inline-block min-w-full align-middle">
                              <MarkdownFormatter
                                content={nextTable.tableContent}
                                className="text-[14px] text-brand-primary-800 font-normal prose-strong:font-medium"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end mt-2">
                            <Button
                              variant="link"
                              size="sm"
                              className="text-gray-700 hover:text-gray-900 underline text-sm p-0"
                              onClick={() => {
                                const filename = `table-data-${format(new Date(), "yyyy-MM-dd")}`;
                                const csvContent = convertTableToCSV(
                                  nextTable.tableContent,
                                );
                                const blob = new Blob([csvContent], {
                                  type: "text/csv;charset=utf-8;",
                                });
                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.setAttribute("download", `${filename}.csv`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              Download CSV
                            </Button>
                          </div>
                        </div>,
                      );
                      currentPosition =
                        nextTable.startIndex + nextTable.tableContent.split("\n").length;
                    } else {
                      currentPosition = lines.length;
                    }
                  }

                  // Add extracted media below the content
                  if (media.length > 0) {
                    const files = convertMediaToFiles(media);
                    contentParts.push(
                      <div key="answer-images" className="mt-4">
                        <FileAttachmentsPreview
                          files={files}
                          className="flex flex-wrap gap-2"
                          showFileName
                        />
                      </div>,
                    );
                  }

                  return contentParts.length > 0 ? (
                    contentParts
                  ) : (
                    <MarkdownFormatter
                      content={response}
                      className="text-[14px] text-brand-primary-800 font-normal prose-strong:font-medium"
                    />
                  );
                })()}
              </div>
            )}
          </div>
        ) : (
          // Legacy single response format
          (() => {
            const { tables } = extractTables(msg.response);
            const media = extractMedia(msg.response);
            const lines = msg.response.split("\n");
            const contentParts = [];
            let currentIndex = 0;
            let currentPosition = 0;

            // Process content and tables in order of appearance
            while (currentPosition < lines.length) {
              // Find the next table that starts after current position
              const nextTable = tables.find((t) => t.startIndex >= currentPosition);

              // If there's content before the next table (or end if no more tables)
              const endPosition = nextTable ? nextTable.startIndex : lines.length;
              const textContent = lines.slice(currentPosition, endPosition).join("\n");

              if (textContent.trim()) {
                contentParts.push(
                  <MarkdownFormatter
                    key={`content-${currentIndex++}`}
                    content={textContent}
                    className={cn(
                      "text-[14px] text-brand-primary-800 font-normal prose-strong:font-medium",
                      msg.agent === Agent.user ? "font-medium" : "",
                    )}
                  />,
                );
              }

              // Add the table if we found one
              if (nextTable) {
                contentParts.push(
                  <div key={`table-${currentIndex++}`} className="mb-4">
                    <div className="overflow-x-auto max-w-full">
                      <div className="inline-block min-w-full align-middle">
                        <MarkdownFormatter
                          content={nextTable.tableContent}
                          className={cn(
                            "text-[14px] text-brand-primary-800 font-normal prose-strong:font-medium",
                            msg.agent === Agent.user ? "font-medium" : "",
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-gray-700 hover:text-gray-900 underline text-sm p-0"
                        onClick={() => {
                          const filename = `table-data-${format(new Date(), "yyyy-MM-dd")}`;
                          const csvContent = convertTableToCSV(nextTable.tableContent);
                          const blob = new Blob([csvContent], {
                            type: "text/csv;charset=utf-8;",
                          });
                          const link = document.createElement("a");
                          link.href = URL.createObjectURL(blob);
                          link.setAttribute("download", `${filename}.csv`);
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download CSV
                      </Button>
                    </div>
                  </div>,
                );
                currentPosition =
                  nextTable.startIndex + nextTable.tableContent.split("\n").length;
              } else {
                currentPosition = lines.length;
              }
            }

            // Add extracted media below the legacy response content
            if (media.length > 0) {
              const files = convertMediaToFiles(media);
              contentParts.push(
                <div key="legacy-images" className="mt-4">
                  <FileAttachmentsPreview
                    files={files}
                    className="flex flex-wrap gap-2"
                    showFileName
                  />
                </div>,
              );
            }

            return contentParts.length > 0 ? (
              contentParts
            ) : (
              <MarkdownFormatter
                content={msg.response}
                className={cn(
                  "text-[14px] text-brand-primary-new/80 font-normal prose-strong:font-medium",
                  msg.agent === Agent.user ? "font-medium" : "",
                )}
              />
            );
          })()
        )}
      </div>
      {msg.annotations && msg.annotations.length > 0 && (
        <div className="mt-2">
          {msg.annotations.map((annotation) => {
            if (annotation.type === "text_annotation") {
              return null;
            }
            const isImage = isImageFile(annotation.file_link);
            if (isImage) {
              return (
                <a
                  href={annotation.file_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={annotation.file_id}
                >
                  <img
                    src={annotation.file_link}
                    alt={annotation.file_name}
                    className="w-full h-auto"
                  />
                </a>
              );
            }
            return (
              <a
                href={annotation.file_link}
                target="_blank"
                rel="noopener noreferrer"
                key={annotation.file_id}
              >
                {annotation.file_name}
              </a>
            );
          })}
        </div>
      )}
      {msg.attachments && msg.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 w-fit">
          <FileAttachmentsPreview files={msg.attachments} />
        </div>
      )}

      {msg.agent === Agent.assistant && (
        <div className={`flex items-center justify-end`}>
          {index === totalMessages - 1 &&
            chatId &&
            (msg.chat_conversation_id || msg.assistant_chat_conversation_id) && (
              <InsightChatFeedback
                key={msg.chat_conversation_id || msg.assistant_chat_conversation_id}
                currentFeedback={
                  msg.feedback || {
                    liked: null,
                    description: "",
                  }
                }
                chatConversationId={
                  msg.chat_conversation_id || msg.assistant_chat_conversation_id || ""
                }
                updateFeedback={handleUpdateFeedback}
              />
            )}
        </div>
      )}
    </div>
  );
}
